import { createContext, useContext, useState, type ReactNode, type JSX } from 'react';

interface FixedHeaderContextValue {
  headerHeight: number;
  setHeaderHeight: (height: number) => void;
  headerBannerHeight: number;
  visibleHeaderHeight: number;
  setVisibleHeaderHeight: (height: number) => void;
  setHeaderBannerHeight: (height: number) => void;
  shouldHeaderOverlapTop: boolean;
  /**
   * Single source of truth for how far content must be pushed down to clear the
   * translucent/overlapping header. Zero when the header does not overlap.
   */
  headerOverlapOffset: number;
  isNavHidden: boolean;
  setIsNavHidden: (isHidden: boolean) => void;
}

const FixedHeaderContext = createContext<FixedHeaderContextValue | undefined>(undefined);

export const useFixedHeaderHeight = (): FixedHeaderContextValue => {
  const context = useContext(FixedHeaderContext);
  if (!context) {
    throw new Error('useFixedHeaderHeight must be used within a FixedHeaderProvider');
  }
  return context;
};

interface FixedHeaderProviderProps {
  children: ReactNode;
  shouldHeaderOverlapTop: boolean;
}

export const FixedHeaderProvider = ({
  children,
  shouldHeaderOverlapTop,
}: FixedHeaderProviderProps): JSX.Element => {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [headerBannerHeight, setHeaderBannerHeight] = useState<number>(0);
  const [visibleHeaderHeight, setVisibleHeaderHeight] = useState<number>(0);
  const [isNavHidden, setIsNavHidden] = useState<boolean>(false);

  const headerOverlapOffset = shouldHeaderOverlapTop ? headerHeight + headerBannerHeight : 0;

  return (
    <FixedHeaderContext.Provider
      value={{
        headerHeight,
        setHeaderHeight,
        visibleHeaderHeight,
        headerBannerHeight,
        setHeaderBannerHeight,
        setVisibleHeaderHeight,
        shouldHeaderOverlapTop,
        headerOverlapOffset,
        isNavHidden,
        setIsNavHidden,
      }}
    >
      {children}
    </FixedHeaderContext.Provider>
  );
};
